<template>
    <app-layout v-if="isPageReady">
        <common-content-spacer height="medium">
            <common-content-spacer height="small">
                <common-breadcrumbs :breadcrumbs="pageBreadcrumbs"></common-breadcrumbs>

                <common-page-header :heading="pageTitle"></common-page-header>
            </common-content-spacer>

            <common-page-split>
                <template #aside>
                    <common-content-spacer height="medium">
                        <user-card
                            v-if="physician.specialities && referral.toUser"
                            class="view-report__user-card"
                            :physician="physician"
                        ></user-card>

                        <practice-card
                            v-if="!referral.toUser"
                            class="view-report__user-card"
                            :practice="referral.practice"
                            :isStandalone="false"
                        ></practice-card>

                        <common-content-group>
                            <common-content-spacer height="medium">
                                <common-icon-info icon="location-blue" size="large">
                                    <common-location-address :location="referral.practiceLocation"></common-location-address>
                                </common-icon-info>

                                <common-icon-info :href="`mailto:${email}`" icon="email" size="huge">
                                    {{ email }}
                                </common-icon-info>
                            </common-content-spacer>
                        </common-content-group>
                    </common-content-spacer>
                </template>

                <template #main>
                    <common-content-spacer height="medium">
                        <div class="view-report__patient-fragment">
                            <h4>
                                Patient:
                                <common-patient-name :patient="referral.patient"></common-patient-name>
                            </h4>
                            <p>{{ $t('pages.referrals.notes.view.seen_on', { date: appointmentDateTime }) }}</p>
                        </div>
                        <common-content-spacer v-if="referral.consultationReports.length" height="medium">
                            <div class="view-report__documents">
                                <embed v-if="fileUrl" :src="fileUrl" width="100%" height="550px" />
                            </div>

                            <div class="auth-sign-up-confirm-practice">
                                <common-pagination
                                    :numberOfItems="referral.consultationReports.length"
                                    @page-changed="documentChanged($event)"
                                ></common-pagination>
                            </div>

                            <common-buttons>
                                <common-button
                                    :isSubmittable="false"
                                    :label="$t('pages.referrals.notes.view.buttons.open_in_new_tab')"
                                    type="secondary"
                                    size="large"
                                    @click="openInNewTab"
                                ></common-button>
                                <common-button
                                    :isSubmittable="false"
                                    :label="$t('pages.referrals.notes.view.buttons.download_report')"
                                    type="primary"
                                    size="large"
                                    @click="downloadReport"
                                ></common-button>
                            </common-buttons>
                        </common-content-spacer>

                        <common-content-spacer v-else class="view-report__warning" height="small">
                            <common-warning
                                size="small"
                                class="view-report__report-not-available"
                                :text="$t('pages.referrals.notes.view.report_not_available')"
                            ></common-warning>
                            <h2>{{ $t('pages.referrals.notes.view.report_expired') }}</h2>
                            <p>{{ $t('pages.referrals.notes.view.report_store_time') }}</p>
                        </common-content-spacer>
                    </common-content-spacer>
                </template>
            </common-page-split>
        </common-content-spacer>
    </app-layout>
</template>

<script>
    import get from 'lodash.get'
    import dayjs from 'dayjs'
    import PageWithTitleMixin from 'mixins/PageWithTitleMixin'
    import PageWithElementsMixin from 'mixins/PageWithElementsMixin'
    import PageWithDataMixin from 'mixins/PageWithDataMixin'
    import FileMixin from 'mixins/FileMixin'
    import AppLayout from 'components/layouts/AppLayout'
    import ReferralsAPI from 'api/ReferralsAPI'
    import PhysiciansAPI from 'api/PhysiciansAPI'
    import UserCard from 'components/page-elements/referrals/UserCard'
    import PracticeCard from 'components/page-elements/practice/PracticeCard'
    import PracticeTypeEnum from '@/enums/PracticeTypeEnum'

    export default {
        mixins: [PageWithTitleMixin, PageWithElementsMixin, PageWithDataMixin, FileMixin],

        components: {
            AppLayout,
            UserCard,
            PracticeCard,
        },

        data() {
            return {
                referral: {},
                physician: {},
                selectedFile: '',
                fileUrl: null,
            }
        },

        loadData(self) {
            let route = self.$route
            return {
                referral: ReferralsAPI.get(route.params.id, self.$auth.getActiveMember().id),
            }
        },

        computed: {
            pageTitle() {
                if (this.referral.practice?.practiceType === PracticeTypeEnum.ANCILLARY) {
                    return this.$t('pages.referrals.notes.view.heading', {
                        firstName: get(this.referral, 'practice.name'),
                        lastName: '',
                    })
                } else {
                    return this.$t('pages.referrals.notes.view.heading', {
                        firstName: get(this.referral, 'toUser.firstName'),
                        lastName: get(this.referral, 'toUser.lastName'),
                    })
                }
            },

            email() {
                if (this.referral.practice.practiceType === 'ancillary') {
                    return this.referral.practice.email
                } else {
                    return this.physician.email
                }
            },

            appointmentDateTime() {
                return dayjs(this.referral.appointmentDateTime).format('MMMM D, YYYY')
            },

            pageBreadcrumbs() {
                return [
                    {
                        label: this.$t('pages.referrals.index.breadcrumbs.referrals'),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t(`pages.referrals.index.breadcrumbs.${this.$route.params.type}`),
                        route: { name: `referrals.${this.$route.params.type}.referrals` },
                    },
                    {
                        label: this.$t('pages.referrals.notes.view.breadcrumb', {
                            firstName: get(this.referral, 'patient.firstName'),
                            lastName: get(this.referral, 'patient.lastName'),
                            date: this.appointmentDateTime,
                        }),
                    },
                ]
            },
        },

        methods: {
            getPhysicianData(physicianId) {
                PhysiciansAPI.getPhysicianData({ userId: this.$auth.getActiveMember().id, physicianId }).then(
                    (res) => (this.physician = res),
                )
            },

            isPDF(file) {
                return file.extension === 'pdf'
            },

            documentChanged(event) {
                this.selectedFile = this.referral.consultationReports[event - 1]
                this.getSelectedImage()
            },

            getSelectedImage() {
                const url = this.selectedFile.url
                this.fetchPrivateBlob(url).then((blob) => {
                    this.fileUrl = window.URL.createObjectURL(blob)
                })
            },

            openInNewTab() {
                const routeData = this.$router.resolve({ name: 'image.preview', query: { url: this.selectedFile.url } })
                window.open(routeData.href, '_blank')
            },

            downloadReport() {
                this.downloadFile(this.selectedFile.url, this.selectedFile.note)
            },
        },

        watch: {
            referral() {
                this.selectedFile = this.referral.consultationReports[0]
                this.getSelectedImage()

                const physicianId = this.$route.params.type === 'received' ? this.referral.fromUser.id : this.referral.toUser?.id

                if (physicianId) {
                    this.getPhysicianData(physicianId)
                }
            },
        },
    }
</script>

<style lang="scss" scoped>
    .view-report {
        &__user-card {
            min-width: 260px;
            min-height: 380px;
        }

        &__patient-fragment {
            font-size: 18px;
            line-height: 35px;

            h4 {
                color: #333;
            }
        }

        &__documents {
            text-align: center;
        }

        &__report-not-available {
            width: fit-content;
        }

        &__warning {
            display: flex;
            flex-direction: column;
            align-items: center;
            color: #333333;
            padding-top: 5%;

            p {
                font-size: 18px;
            }
        }
    }
</style>
